import React, { useState, useRef, useEffect } from "react";
import { buildChain, roundMaxItems } from "../lib/chain";
import { createPortal } from "react-dom";
const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const ANGLE_LABELS = { former: "Formers", competitor: "Competitors", customer: "Customers" };

// ── Source flow D: presentation constants (UI only) ──────────────────────────
// Step index per stage; step 4 ("done") means all four pills are complete.
const STEP_OF = { configure: 0, proposing: 0, signoff: 1, probing: 1, counts: 2, pulling: 3, done: 4 };
const STEP_LABELS = ["Configure", "Sign off", "Counts", "Fetch"];
const STAGE_META = {
  configure: "Step 1 of 4",
  proposing: "Step 1 of 4 · working…",
  signoff: "Step 2 of 4",
  probing: "Step 2 of 4 · working…",
  counts: "Step 3 of 4",
  pulling: "Step 4 of 4 · working…",
  done: "Done",
};
const STAGE_COPY = {
  configure: ["Set up your search", "Tell us the company and what you want to learn. Rasp drafts the rest."],
  proposing: ["Drafting your plan", "This takes a few seconds."],
  signoff: ["Review the plan", "Change anything you like — nothing has been fetched yet."],
  probing: ["Counting matches", "A quick check before anything is pulled."],
  counts: ["Choose how many to bring in", "You stay in control of coverage and cost."],
  pulling: ["Bringing in your leads", "This can take a minute or two."],
  done: ["You're all set", "Your new batch is ready to rank."],
};
const ANGLE_HINT = "Formers = people who used to work there. Competitors and customers search adjacent companies instead.";
// Async stage → the stage the real machine moves to when it succeeds. The loading
// screen holds ~350ms on "complete" before the next stage is shown.
const ASYNC_NEXT = { proposing: "signoff", probing: "counts", pulling: "done" };
const COMPLETE_HOLD_MS = 350;
// Expected durations drive the ring and ETA only where no real progress exists.
// They are estimates, not measurements — tune from real runs.
const EXPECT_MS = { proposing: 8000, probeSet: 14000, pullRun: 90000 };
const LI_PAGE_SIZE = 25;
const SNAPSHOT_KEY = "nsp_inflight_v1";
const RING_C = 2 * Math.PI * 54; // r=54
const extractDomain = (url) => {
  if (!url) return url;
  try { return new URL(url).hostname; } catch { return url; }
};

const fmtN = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US"));
const makeCacheKey = (cs) => JSON.stringify({ t: [...cs.titles].sort(), e: [...cs.exclude].sort() });
const wordCount = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;

// ── Company dedup helpers (mirrors backend _is_same_company) ──────────────────
const CO_SUFFIX_TOKENS = new Set(['inc','llc','ltd','corp','co','company','group','holdings','plc','gmbh','sas','bv']);
const normCompany = (name) => {
  const tokens = name.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/).filter(Boolean);
  return new Set(tokens.filter((t) => !CO_SUFFIX_TOKENS.has(t)));
};
const normLinkedInSlug = (url) => {
  if (!url) return null;
  const m = url.toLowerCase().split('?')[0].split('#')[0].match(/\/company\/([^/]+)/);
  return m ? m[1].replace(/\/+$/, '') : null;
};
const sameCompany = (a, b) => {
  const as_ = normLinkedInSlug(a.url);
  const bs_ = normLinkedInSlug(b.url);
  if (as_ && bs_) return as_ === bs_;
  const at = normCompany(a.name);
  const bt = normCompany(b.name);
  if (at.size !== bt.size) return false;
  for (const t of at) if (!bt.has(t)) return false;
  return true;
};

// ── Chip popover (portal) ─────────────────────────────────────────────────────
function ChipPopover({ anchor, chip, onSave, onRemoveLink, onRemoveChip, onClose }) {
  const ref = useRef(null);
  const [urlInput, setUrlInput] = useState(chip.url || "");
  const [urlError, setUrlError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  const validate = (v) =>
    v.trim() && !v.includes("linkedin.com/company/")
      ? "Must be a LinkedIn company URL (linkedin.com/company/…)"
      : null;

  const handleSave = () => {
    const err = validate(urlInput);
    if (err) { setUrlError(err); return; }
    onSave(urlInput.trim() || null);
  };

  const left = Math.min(Math.max(8, anchor.left), window.innerWidth - 282);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: anchor.bottom + 6,
        left,
        zIndex: 9999,
        minWidth: 274,
        background: "var(--glass-card)",
        border: "1px solid var(--line)",
        borderRadius: 10,
        padding: 12,
        boxShadow: "var(--card-shadow)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)", marginBottom: 6 }}>
        {chip.name}
      </div>
      <input
        autoFocus
        type="text"
        placeholder="linkedin.com/company/…"
        value={urlInput}
        onChange={(e) => { setUrlInput(e.target.value); setUrlError(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
        style={{ width: "100%", boxSizing: "border-box", height: 32, background: "var(--input-bg)", border: "1px solid var(--line)", borderRadius: 8, padding: "0 10px", fontSize: 12, color: "var(--text-1)", marginBottom: urlError ? 2 : 6 }}
      />
      {urlError && (
        <div style={{ fontSize: 10, color: "var(--negative)", marginBottom: 4 }}>{urlError}</div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button className="r-btn r-btn-primary" onClick={handleSave} style={{ height: 26, fontSize: 11, padding: "0 10px" }}>
          Save link
        </button>
        {chip.url && (
          <button
            onClick={() => { onRemoveLink(); onClose(); }}
            className="r-btn r-btn-ghost"
            style={{ height: 26, fontSize: 11 }}
          >
            Remove link
          </button>
        )}
        <button
          onClick={() => { onRemoveChip(); onClose(); }}
          className="r-btn r-btn-ghost"
          style={{ height: 26, fontSize: 11, color: "var(--negative)" }}
        >
          Remove chip
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── Chip ──────────────────────────────────────────────────────────────────────
// tone: undefined = default chip; "committed" = configure-stage company chip
// (teal, dot, ×); "target" = sign-off company chip (amber + hollow dot until
// linked, then teal + dot + "linked").
function Chip({ chip, onUpdate, onRemove, warnMixed = false, proposed = false, struck = false, onStrike = null, onClear = null, tone }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const ref = useRef(null);

  const isLinked = !!chip.url;

  const border = struck
    ? "1px solid var(--line)"
    : tone === "committed"
    ? "1px solid rgba(45,212,191,.28)"
    : tone === "target"
    ? (isLinked ? "1px solid rgba(45,212,191,.28)" : "1px solid rgba(224,160,80,.5)")
    : warnMixed
    ? "1px solid var(--warning)"
    : isLinked
    ? "1px solid rgba(45,212,191,.28)"
    : proposed
    ? "1px dashed rgba(45,212,191,.28)"
    : "1px solid var(--line)";

  const bg = !struck && (isLinked || tone === "committed") ? "rgba(45,212,191,.08)" : "var(--input-bg)";
  const dotFilled = tone === "committed" || isLinked;

  const handleClick = () => {
    if (struck) return;
    if (ref.current) setAnchor(ref.current.getBoundingClientRect());
    setOpen(true);
  };

  return (
    <>
      <div
        ref={ref}
        onClick={handleClick}
        title={
          struck ? chip.name
          : warnMixed ? "Name-match only — link its LinkedIn page for exact matching."
          : isLinked ? chip.url
          : "Click to add LinkedIn URL"
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "0 8px",
          height: 24,
          borderRadius: 4,
          border,
          background: bg,
          fontSize: 12,
          color: struck ? "var(--text-5)" : "var(--text-1)",
          cursor: struck ? "default" : "pointer",
          textDecoration: struck ? "line-through" : "none",
          opacity: struck ? 0.5 : 1,
          userSelect: "none",
          flexShrink: 0,
          transition: "border-color .25s, background .25s",
          animation: tone === "committed" ? "nsp-popIn .25s ease-out" : undefined,
        }}
      >
        {!struck && (
          <span style={{
            width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
            background: dotFilled ? "var(--accent)" : "none",
            border: dotFilled ? "none" : "1px solid var(--text-3)",
          }} />
        )}
        <span>{chip.name}</span>
        {tone === "target" && isLinked && (
          <span style={{ fontSize: 10, fontFamily: "var(--font-data)", color: "var(--accent)" }}>linked</span>
        )}
        {proposed && !struck && (
          <span style={{ fontSize: 10, fontFamily: "var(--font-data)", color: "var(--accent)", marginLeft: 1 }}>proposed</span>
        )}
        {onStrike && (
          <span
            onClick={(e) => { e.stopPropagation(); onStrike(); }}
            style={{ marginLeft: 2, cursor: "pointer", color: "var(--text-4)", lineHeight: 1, fontSize: 13 }}
          >
            {struck ? "↩" : "×"}
          </span>
        )}
        {onClear && (
          <span
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            title="Clear"
            style={{ marginLeft: 2, cursor: "pointer", color: "var(--text-4)", lineHeight: 1, fontSize: 13 }}
          >
            ×
          </span>
        )}
      </div>
      {open && anchor && (
        <ChipPopover
          anchor={anchor}
          chip={chip}
          onSave={(url) => { onUpdate({ ...chip, url }); setOpen(false); }}
          onRemoveLink={() => onUpdate({ ...chip, url: null })}
          onRemoveChip={() => { onRemove(); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── Small presentational pieces ───────────────────────────────────────────────
function Tick({ color = "#04201d", w = 4, h = 8, stroke = 1.5, mt = -2 }) {
  return (
    <span style={{
      width: w, height: h, display: "block", marginTop: mt,
      borderRight: `${stroke}px solid ${color}`, borderBottom: `${stroke}px solid ${color}`,
      transform: "rotate(45deg)",
    }} />
  );
}

function StepPills({ step }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {STEP_LABELS.map((label, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <span key={label} style={{ display: "contents" }}>
            <span
              data-step-state={done ? "done" : active ? "active" : "todo"}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8, height: 30,
                padding: "0 12px 0 6px", borderRadius: 999, fontSize: 12, fontWeight: 500,
                border: `1px solid ${done ? "rgba(45,212,191,.3)" : active ? "rgba(45,212,191,.5)" : "rgba(130,210,220,.14)"}`,
                background: active ? "rgba(45,212,191,.10)" : "transparent",
                color: done || active ? "var(--text-1)" : "var(--text-4)",
                transition: "all .4s",
              }}
            >
              <span style={{
                width: 20, height: 20, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                background: done ? "var(--accent)" : active ? "rgba(45,212,191,.12)" : "transparent",
                border: `1px solid ${done ? "var(--accent)" : active ? "rgba(45,212,191,.6)" : "var(--line)"}`,
                color: active ? "var(--accent)" : "var(--text-4)",
                fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 500,
              }}>
                {done ? <Tick /> : i + 1}
              </span>
              {label}
            </span>
            {i < STEP_LABELS.length - 1 && (
              <span style={{ width: 14, height: 1, background: done ? "var(--accent)" : "var(--line)", transition: "background .4s" }} />
            )}
          </span>
        );
      })}
    </div>
  );
}

function ChecklistRow({ label, state, value, valueColor }) {
  const isDone = state === "done";
  const isActive = state === "active";
  const border = isDone ? "rgba(69,196,154,.3)" : isActive ? "rgba(45,212,191,.45)" : "rgba(130,210,220,.14)";
  return (
    <div data-row-state={state} style={{
      display: "flex", alignItems: "center", gap: 10, minHeight: 40, padding: "0 14px", borderRadius: 10,
      border: `1px solid ${border}`,
      background: isActive ? "rgba(45,212,191,.08)" : "rgba(255,255,255,.02)",
      fontSize: 13, color: state === "todo" ? "var(--text-4)" : "var(--text-1)", transition: "all .4s",
    }}>
      <span style={{
        width: 18, height: 18, borderRadius: "50%", flex: "none", display: "flex", alignItems: "center", justifyContent: "center",
        background: isDone ? "var(--positive)" : "transparent", border: `1px solid ${border}`,
      }}>
        {isDone && <Tick w={4} h={7} mt={-1} />}
        {isActive && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)", display: "block", animation: "rasp-pulse 1s ease-in-out infinite" }} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {value != null && value !== "" && (
        <span style={{ fontFamily: "var(--font-data)", fontSize: 12, fontWeight: 500, color: valueColor || "var(--text-1)", flex: "none" }}>{value}</span>
      )}
    </div>
  );
}

// Full-panel loading screen used by proposing / probing / pulling.
function LoadingScreen({ title, status, reassurance, progress, rows, eta }) {
  const p = Math.max(0, Math.min(1, progress || 0));
  return (
    <div data-testid="nsp-loading" style={{
      minHeight: 340, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 22, padding: "40px 28px 48px", animation: "nsp-slideIn .4s cubic-bezier(.2,.7,.2,1)",
    }}>
      <div style={{ position: "relative", width: 120, height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="120" height="120" viewBox="0 0 120 120" style={{ position: "absolute", inset: 0, transform: "rotate(-90deg)" }}>
          <defs>
            <linearGradient id="nspRingGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#0e7490" />
              <stop offset="1" stopColor="#5eead4" />
            </linearGradient>
          </defs>
          <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="5" />
          <circle
            data-testid="nsp-ring"
            cx="60" cy="60" r="54" fill="none" stroke="url(#nspRingGrad)" strokeWidth="5" strokeLinecap="round"
            strokeDasharray={RING_C.toFixed(1)} strokeDashoffset={(RING_C * (1 - p)).toFixed(1)}
            style={{ transition: "stroke-dashoffset .5s ease" }}
          />
        </svg>
        <span style={{
          width: 56, height: 56, borderRadius: 18, background: "linear-gradient(135deg,#2dd4bf,#0e7490)",
          boxShadow: "0 0 30px rgba(45,212,191,.4)", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", display: "block", animation: "rasp-pulse 1.6s ease-in-out infinite" }} />
        </span>
      </div>
      <div style={{ textAlign: "center", maxWidth: 520 }}>
        <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.015em", color: "var(--text-1)" }}>{title}</div>
        <div style={{ fontSize: 14, color: "var(--text-2)", marginTop: 8, lineHeight: 1.5, minHeight: 22 }}>{status}</div>
        <div style={{ fontSize: 12, color: "var(--text-4)", marginTop: 6 }}>{reassurance}</div>
      </div>
      {rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "min(440px, 100%)" }}>
          {rows.map((r, i) => <ChecklistRow key={i} {...r} />)}
        </div>
      )}
      <span style={{ fontSize: 12, color: "var(--text-4)" }}>{eta}</span>
    </div>
  );
}

function SideNote({ sections }) {
  return (
    <div style={{
      flex: "1 1 260px", maxWidth: 320, alignSelf: "flex-start", border: "1px solid var(--line-soft)", borderRadius: 8,
      padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12, fontSize: 12, color: "var(--text-3)",
      background: "var(--subbar-bg)",
    }}>
      {sections.map(([heading, body], i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {i > 0 && <div style={{ height: 1, background: "rgba(130,210,220,.10)" }} />}
          {heading && <div className="r-eyebrow">{heading}</div>}
          <div style={{ lineHeight: 1.55 }}>{body}</div>
        </div>
      ))}
    </div>
  );
}

const Mono = ({ children, color = "var(--text-1)" }) => (
  <span style={{ fontFamily: "var(--font-data)", color }}>{children}</span>
);

const autoSize = (el) => { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; };

// ── NewSearchPanel ─────────────────────────────────────────────────────────────
// onStageChange(stage, batchId) and onOpenBatch(batchId) are optional, UI-only
// callbacks (Source flow D): SourceView uses them to dim the batch list while a
// search runs, highlight the new batch, and open it from "Rank my leads".
export default function NewSearchPanel({ token, onIngested, onStageChange, onOpenBatch, rankStatus }) {
  const [stage, setStage] = useState("configure");

  // Configure inputs — preserved through Back
  const [angle, setAngle] = useState("former");
  const [targetChip, setTargetChip] = useState(null);
  const [targetInput, setTargetInput] = useState("");
  const [companyChips, setCompanyChips] = useState([]);
  const [companyInput, setCompanyInput] = useState("");
  const [titlesInput, setTitlesInput] = useState("");
  const [contextInput, setContextInput] = useState("");
  const [excludeHr, setExcludeHr] = useState(true);
  const [excludeJunior, setExcludeJunior] = useState(true);

  // Signoff state — populated from propose response
  const [planWarnings, setPlanWarnings] = useState([]);
  const [signoffTarget, setSignoffTarget] = useState(null);
  const [signoffCompanies, setSignoffCompanies] = useState([]);
  const [sfCompanyInput, setSfCompanyInput] = useState("");
  const [addDupWarning, setAddDupWarning] = useState(null);
  const [sets, setSets] = useState([{ titles: "" }]);
  const [searchContext, setSearchContext] = useState("");
  const [sfExcludeHr, setSfExcludeHr] = useState(true);
  const [sfExcludeJunior, setSfExcludeJunior] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Captured propose response — sent as plan in batch POST
  const [proposedPlan, setProposedPlan] = useState(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // Runtime
  const [batchId, setBatchId] = useState(null);
  const [probeSets, setProbeSets] = useState([]);
  const [probeResults, setProbeResults] = useState([]);
  const [fetchCaps, setFetchCaps] = useState([]);
  const [groupEnabled, setGroupEnabled] = useState([]);
  const [uniqueCounts, setUniqueCounts] = useState(null);
  const [uniqueNeedsUpdate, setUniqueNeedsUpdate] = useState(false);
  const [uniqueUpdating, setUniqueUpdating] = useState(false);
  const [countsExpandedGroups, setCountsExpandedGroups] = useState(new Set());
  const [uniqueCountsCache, setUniqueCountsCache] = useState({});
  const [autoRecountSignal, setAutoRecountSignal] = useState(0);
  const [probingUnique, setProbingUnique] = useState(false);
  const [pullResults, setPullResults] = useState([]);
  const [pullRuns, setPullRuns] = useState([]);
  const [progressMsg, setProgressMsg] = useState("");
  const [error, setError] = useState(null);
  const [tightenBanner, setTightenBanner] = useState(null); // {text, prevSets}
  const [tightenLoading, setTightenLoading] = useState(false);
  const [tightenError, setTightenError] = useState(null);
  const [creditsMe, setCreditsMe] = useState(null);

  const autoRankCalledRef = useRef(false);
  const autoRecountTimerRef = useRef(null);

  // ── UI-only state (Source flow D) — drives presentation, never an API call ──
  const [shownStage, setShownStage] = useState(stage);
  const [holding, setHolding] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [probeLive, setProbeLive] = useState([]);   // probe results as each set finishes
  const [pullLive, setPullLive] = useState({});     // pull results keyed by page index as each run finishes
  const [now, setNow] = useState(() => Date.now());
  const stageStartRef = useRef(Date.now());
  const probeStepStartRef = useRef(Date.now());
  const busy = !!ASYNC_NEXT[shownStage];

  // Follow the real stage; after an async stage succeeds, hold its loading screen
  // on "complete" for COMPLETE_HOLD_MS before showing the next stage.
  useEffect(() => {
    if (stage === shownStage) { setHolding(false); return undefined; }
    if (ASYNC_NEXT[shownStage] === stage && !error) {
      setHolding(true);
      const t = setTimeout(() => { setHolding(false); setShownStage(stage); }, COMPLETE_HOLD_MS);
      return () => clearTimeout(t);
    }
    setHolding(false);
    setShownStage(stage);
    return undefined;
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    stageStartRef.current = Date.now();
    probeStepStartRef.current = Date.now();
    setNow(Date.now());
  }, [shownStage]);
  useEffect(() => { probeStepStartRef.current = Date.now(); }, [probeLive.length]);
  useEffect(() => {
    if (!busy || holding) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [busy, holding]);
  useEffect(() => {
    if (onStageChange) onStageChange(shownStage, batchId);
  }, [shownStage, batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch credits balance when first entering the counts stage.
  // creditsMe guard: skip on re-entry (e.g. after a 402 returned updated balance).
  useEffect(() => {
    if (stage !== "counts" || creditsMe !== null) return;
    (async () => {
      try {
        const r = await fetch(`${API}/credits/me`, { headers });
        if (r.ok) {
          const data = await r.json();
          if (typeof data.balance === 'number') setCreditsMe(data);
        }
      } catch (_) {}
    })();
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // V2: auto-recount 1,200ms after the last toggle.
  useEffect(() => {
    if (autoRecountSignal === 0) return;
    const anyEnabled = probeSets.some((_, i) => groupEnabled[i] ?? true);
    if (!anyEnabled) return;
    handleUpdateUnique();
  }, [autoRecountSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist in-flight search so navigation doesn't lose progress.
  useEffect(() => {
    if (stage === "done") {
      try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (_) {}
      return;
    }
    if (stage !== "counts" && stage !== "pulling") return;
    try {
      sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({
        stage, batchId, angle, signoffTarget, signoffCompanies,
        sets, searchContext, sfExcludeHr, sfExcludeJunior,
        probeSets, probeResults, fetchCaps, groupEnabled, uniqueCounts,
      }));
    } catch (_) {}
  }, [stage, batchId, angle, signoffTarget, signoffCompanies, sets, searchContext, sfExcludeHr, sfExcludeJunior, probeSets, probeResults, fetchCaps, groupEnabled, uniqueCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: restore a snapshot left by a previous navigation.
  useEffect(() => {
    let snap;
    try { snap = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || "null"); } catch (_) {}
    if (!snap || (snap.stage !== "counts" && snap.stage !== "pulling")) return;
    setBatchId(snap.batchId ?? null);
    setAngle(snap.angle ?? "former");
    setSignoffTarget(snap.signoffTarget ?? null);
    setSignoffCompanies(snap.signoffCompanies ?? []);
    setSets(snap.sets ?? [{ titles: "" }]);
    setSearchContext(snap.searchContext ?? "");
    setSfExcludeHr(snap.sfExcludeHr ?? true);
    setSfExcludeJunior(snap.sfExcludeJunior ?? true);
    setProbeSets(snap.probeSets ?? []);
    setProbeResults(snap.probeResults ?? []);
    setFetchCaps(snap.fetchCaps ?? []);
    if (snap.groupEnabled) setGroupEnabled(snap.groupEnabled);
    if (snap.uniqueCounts) setUniqueCounts(snap.uniqueCounts);
    if (snap.stage === "counts") { setStage("counts"); return; }
    // stage === "pulling": restore state and resume polling existing runs
    setStage("pulling");
    setProgressMsg("Resuming…");
    (async () => {
      const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      let runs;
      try {
        const r = await fetch(`${API}/sourcing-batches/${snap.batchId}/apify-runs`);
        if (!r.ok) { setStage("done"); return; }
        runs = await r.json();
      } catch (_) { setStage("done"); return; }
      if (!runs || !runs.length) { setStage("done"); return; }
      const resultArr = runs.map(() => ({ staged: 0, dupes: 0, error: null }));
      const isTerminal = (s) => s === "ingested" || s === "partial" || s === "failed";
      await Promise.allSettled(
        runs.map(async (run, i) => {
          try {
            if (isTerminal(run.status)) {
              resultArr[i] = {
                staged: run.staged ?? 0,
                dupes: run.intra_batch_dupes ?? 0,
                error: run.status === "failed" ? run.error || "Run failed" : null,
              };
            } else {
              while (true) {
                await new Promise((res) => setTimeout(res, 10000));
                const cr = await fetch(`${API}/apify-runs/${run.id}/check`, { method: "POST", headers: h });
                const cd = await cr.json();
                if (isTerminal(cd.status)) {
                  resultArr[i] = {
                    staged: cd.staged ?? 0,
                    dupes: cd.intra_batch_dupes ?? 0,
                    error: cd.status === "failed" ? cd.error || "Run failed" : null,
                  };
                  break;
                }
              }
            }
            setPullLive((p) => ({ ...p, [i]: resultArr[i] }));
          } catch (_) {
            resultArr[i] = { staged: 0, dupes: 0, error: "Polling error" };
            setPullLive((p) => ({ ...p, [i]: resultArr[i] }));
          }
        })
      );
      setPullRuns(runs);
      setPullResults(resultArr);
      setProgressMsg("");
      setStage("done");
      if (onIngested) onIngested();
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // ── Derived ───────────────────────────────────────────────────────────────
  const activeCompanies = signoffCompanies.filter((c) => !c.struck);
  const validSets = sets.filter((s) => s.titles.trim().length > 0);
  const nameMatchCompanies = activeCompanies.filter((c) => !c.url);
  const maxSets = angle === "customer" ? 1 : 4;
  const anyConfigCompanyLinked = companyChips.some((c) => !!c.url);
  const anySignoffCompanyLinked = activeCompanies.some((c) => !!c.url);
  const planSourceCompanies = signoffCompanies.filter((c) => c.proposed && c.source_url);

  const canPropose =
    !!targetChip &&
    (angle !== "customer" || contextInput.trim().length > 0);

  const runsCount = angle === "former" ? validSets.length : activeCompanies.length;

  const unlinkedCount =
    (signoffTarget && !signoffTarget.url ? 1 : 0) +
    (angle !== "former" ? signoffCompanies.filter((c) => !c.struck && !c.url).length : 0);
  const dupCompanyPair = (() => {
    const active = signoffCompanies.filter((c) => !c.struck);
    for (let i = 0; i < active.length; i++)
      for (let j = i + 1; j < active.length; j++)
        if (sameCompany(active[i], active[j])) return [active[i].name, active[j].name];
    return null;
  })();
  const chain = (angle === 'former' && probeSets.length > 0)
    ? buildChain(probeSets.map((ps, i) => ({
        index: i,
        titles: ps.pastJobTitles || [],
        count: (probeResults[i] || {}).totalElements ?? 0,
        enabled: groupEnabled[i] ?? true,
      })))
    : null;
  const uniqueTotal = (angle === 'former' && chain && uniqueCounts && !uniqueNeedsUpdate && !uniqueCounts.some(c => c === null))
    ? chain.reduce((s, cs, k) => {
        const uc = uniqueCounts[k] ?? 0;
        return s + (cs.overLimit ? Math.min(uc, 2500) : uc);
      }, 0)
    : null;
  const totalFetch = angle === 'former'
    ? (chain && uniqueCounts && !uniqueNeedsUpdate && !uniqueCounts.some(c => c === null)
        ? chain.reduce((s, cs, k) => s + roundMaxItems(uniqueCounts[k] ?? 0), 0)
        : 0)
    : fetchCaps.reduce((s, v) => s + (Number(v) || 0), 0);
  const allGroupsOff = angle === 'former' && probeSets.length > 0 && probeSets.every((_, i) => !(groupEnabled[i] ?? true));
  const isFormerStale = angle === 'former' && (uniqueNeedsUpdate || allGroupsOff);
  const failedSearches = (angle === 'former' && chain && uniqueCounts)
    ? chain.reduce((acc, cs, k) => {
        if (uniqueCounts[k] === null) acc.push(cs.search);
        return acc;
      }, [])
    : [];
  const hasUniqueError = failedSearches.length > 0;
  const totalStaged = pullResults.reduce((s, r) => s + (r.staged || 0), 0);
  const totalDupes = pullResults.reduce((s, r) => s + (r.dupes || 0), 0);

  const successfulCounts = probeResults
    .map((r) => r.totalElements)
    .filter((v) => v != null && v > 0);
  const medianCount = (() => {
    if (!successfulCounts.length) return 0;
    const s = [...successfulCounts].sort((a, b) => a - b);
    const m = Math.floor(s.length / 2);
    return s.length % 2 === 0 ? (s[m - 1] + s[m]) / 2 : s[m];
  })();

  // ── Helpers ───────────────────────────────────────────────────────────────
  const parseTitles = (str) => str.split(",").map((t) => t.trim()).filter(Boolean);

  const buildRunLabel = (run) => {
    if (run.meta && run.meta.kind === 'pull') {
      const k = run.meta.search;
      const n = run.meta.of;
      const groups = (run.meta.groups || []).map(g => g + 1).join(', ');
      const startPage = run.start_page != null ? run.start_page : 1;
      const maxItems = run.max_items != null ? run.max_items : LI_PAGE_SIZE;
      const a = (startPage - 1) * LI_PAGE_SIZE + 1;
      const b = a + maxItems - 1;
      return `Search ${k} of ${n} · groups ${groups} · people ${a}–${b}`;
    }
    const startPage = run.start_page != null ? run.start_page : 1;
    const maxItems = run.max_items != null ? run.max_items : LI_PAGE_SIZE;
    const a = (startPage - 1) * LI_PAGE_SIZE + 1;
    const b = a + maxItems - 1;
    const terms = run.companies || [];
    const range = `people ${a}–${b}`;
    if (!terms.length) return range;
    const raw = terms[0];
    const suffix = terms.length > 1 ? ` +${terms.length - 1}` : '';
    let name;
    if (raw.includes('linkedin.com')) {
      const signoffMatch = signoffCompanies.find((c) => c.url === raw);
      if (signoffMatch) {
        name = signoffMatch.name;
      } else {
        const m = raw.match(/linkedin\.com\/company\/([^/]+)/);
        name = m ? m[1].replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : raw;
      }
    } else {
      name = raw;
    }
    return `${name}${suffix} · ${range}`;
  };

  const SENIORITY_WORDS = new Set([
    "vp", "svp", "evp", "avp", "vice", "president", "director", "senior", "sr",
    "manager", "head", "chief", "principal", "lead", "executive", "of", "the", "and",
  ]);
  const isSeniorityOnly = (title) => {
    const tokens = title.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.length > 0 && tokens.every((tok) => SENIORITY_WORDS.has(tok));
  };

  const seniorityOnlyByGroup = sets.map((s) =>
    parseTitles(s.titles).filter(isSeniorityOnly)
  );
  const hasSeniorityOnlyTitles = seniorityOnlyByGroup.some((g) => g.length > 0);

  const canCheckCounts =
    !!signoffTarget?.name &&
    unlinkedCount === 0 &&
    !dupCompanyPair &&
    !hasSeniorityOnlyTitles &&
    (angle === "customer"
      ? activeCompanies.length > 0 && validSets.length > 0
      : angle === "competitor"
      ? validSets.length > 0 && activeCompanies.length > 0
      : validSets.length > 0);

  const pollRun = async (runId) => {
    while (true) {
      await new Promise((r) => setTimeout(r, 10000));
      const res = await fetch(`${API}/apify-runs/${runId}/check`, { method: "POST", headers });
      const data = await res.json();
      if (data.status === "ingested" || data.status === "partial" || data.status === "failed") return data;
    }
  };

  const addTargetChip = () => {
    if (!targetInput.trim() || targetChip) return;
    setTargetChip({ name: targetInput.trim(), url: null });
    setTargetInput("");
  };

  const addCompanyChip = () => {
    if (!companyInput.trim()) return;
    setCompanyChips((p) => [...p, { name: companyInput.trim(), url: null }]);
    setCompanyInput("");
  };

  const addSfCompanyChip = () => {
    if (!sfCompanyInput.trim()) return;
    const newChip = { name: sfCompanyInput.trim(), url: null };
    const dup = signoffCompanies.find((c) => !c.struck && sameCompany(c, newChip));
    if (dup) {
      setAddDupWarning("Already in the list");
      setTimeout(() => setAddDupWarning(null), 3000);
      return;
    }
    setSignoffCompanies((p) => [...p, { ...newChip, proposed: false, struck: false }]);
    setSfCompanyInput("");
  };

  const resetAll = () => {
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (_) {}
    setStage("configure");
    setTargetChip(null);
    setTargetInput("");
    setCompanyChips([]);
    setCompanyInput("");
    setTitlesInput("");
    setContextInput("");
    setExcludeHr(true);
    setExcludeJunior(true);
    setError(null);
    setPlanWarnings([]);
    setBatchId(null);
    setProbeSets([]);
    setProbeResults([]);
    setFetchCaps([]);
    setGroupEnabled([]);
    setUniqueCounts(null);
    setUniqueNeedsUpdate(false);
    setUniqueCountsCache({});
    setCountsExpandedGroups(new Set());
    if (autoRecountTimerRef.current) { clearTimeout(autoRecountTimerRef.current); autoRecountTimerRef.current = null; }
    setPullResults([]);
    setProgressMsg("");
    autoRankCalledRef.current = false;
  };

  // When pull finishes with staged leads and a topic, open the batch for ranking once.
  useEffect(() => {
    if (stage !== 'done') return;
    const staged = pullResults.reduce((s, r) => s + (r.staged || 0), 0);
    if (staged === 0) return;
    if (!searchContext.trim()) return;
    if (!onOpenBatch || batchId == null) return;
    if (autoRankCalledRef.current) return;
    autoRankCalledRef.current = true;
    onOpenBatch(batchId, { rank: true, noExpand: true });
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // When SourcingBatches reports ranking done for our batch, reset to Configure.
  useEffect(() => {
    if (!rankStatus || rankStatus.batchId !== batchId) return;
    if (rankStatus.state === 'done' && stage === 'done') resetAll();
  }, [rankStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlePropose ─────────────────────────────────────────────────────────
  const handlePropose = async () => {
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch (_) {}
    setError(null);
    setStage("proposing");
    try {
      const res = await fetch(`${API}/search-plans/propose`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          angle,
          context: contextInput.trim(),
          target: { name: targetChip.name, url: targetChip.url || null },
          companies: companyChips.map((c) => ({ name: c.name, url: c.url || null })),
          titles: parseTitles(titlesInput),
          exclude_hr: excludeHr,
          exclude_junior: excludeJunior,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setProposedPlan({ ...data, captured: "as-proposed" });
      const p = data.plan;
      setSignoffTarget({ name: p.target.name, url: p.target.url || null });
      setSignoffCompanies(
        (p.company_sets || []).map((c) => ({
          name: c.name,
          url: c.url || null,
          proposed: c.proposed,
          struck: false,
          source_url: c.source_url || null,
        }))
      );
      setSets(
        (p.title_sets || []).length > 0
          ? p.title_sets.map((ts) => ({ titles: ts.join(", ") }))
          : [{ titles: "" }]
      );
      setSearchContext(p.search_context || "");
      setSfExcludeHr(p.exclude_hr !== false);
      setSfExcludeJunior(p.exclude_junior !== false);
      setPlanWarnings(data.warnings || []);
      setSourcesOpen(false);
      setStage("signoff");
    } catch (e) {
      setError(e.message);
      setStage("configure");
    }
  };

  // ── handleProbe ───────────────────────────────────────────────────────────
  const handleProbe = async () => {
    setError(null);
    setUniqueCountsCache({});
    setCountsExpandedGroups(new Set());
    setStage("probing");
    setProbeResults([]);
    setProbeLive([]); // UI-only progress

    const titlesUnion = (() => {
      const seen = new Set();
      const out = [];
      for (const s of validSets) {
        for (const t of parseTitles(s.titles)) {
          const tl = t.toLowerCase();
          if (!seen.has(tl) && out.length < 50) { seen.add(tl); out.push(t); }
        }
      }
      return out;
    })();

    const snap =
      angle === "former"
        ? validSets.map((s) => ({
            label: parseTitles(s.titles)[0] || "Set",
            linked: !!signoffTarget?.url,
            pastJobTitles: parseTitles(s.titles),
            companyName: null,
            companyUrl: null,
            titlesUnion: null,
          }))
        : activeCompanies.map((c) => ({
            label: c.name,
            linked: !!c.url,
            pastJobTitles: null,
            companyName: c.name,
            companyUrl: c.url || null,
            titlesUnion,
          }));
    setProbeSets(snap);

    // Create batch
    let bid;
    try {
      const _signoffBody = {
        target: { name: signoffTarget?.name || '', url: signoffTarget?.url || '' },
        companies: activeCompanies.map(c => ({ name: c.name, url: c.url || '', proposed: c.proposed || false })),
        role_groups: validSets.map(s => parseTitles(s.titles)).filter(g => g.length > 0),
        search_context: searchContext.trim(),
        exclude_hr: sfExcludeHr,
        exclude_junior: sfExcludeJunior,
      };
      const bRes = await fetch(`${API}/sourcing-batches`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          label: `${ANGLE_LABELS[angle]} — ${signoffTarget?.name || ""}`,
          angle,
          target_company: signoffTarget?.name || "",
          ...(searchContext.trim() ? { search_context: searchContext.trim() } : {}),
          ...(proposedPlan !== null ? { plan: proposedPlan } : {}),
          signoff: _signoffBody,
        }),
      });
      const bData = await bRes.json();
      if (!bRes.ok) throw new Error(bData.error || `HTTP ${bRes.status}`);
      bid = bData.id;
      setBatchId(bid);
      if (onIngested) onIngested();
    } catch (e) {
      setError(e.message);
      setStage("signoff");
      return;
    }

    // Probe each set sequentially
    const results = [];
    for (let i = 0; i < snap.length; i++) {
      const ps = snap[i];
      setProgressMsg(`Counting ${i + 1}/${snap.length}: ${ps.label}…`);
      try {
        const runBody = {
          max_items: 1,
          exclude_hr: sfExcludeHr,
          exclude_junior: sfExcludeJunior,
        };
        if (angle === "former") {
          runBody.past_job_titles = ps.pastJobTitles;
          runBody.meta = { kind: "group_count", groups: [i] };
          if (signoffTarget?.url) runBody.company_url = signoffTarget.url;
        } else {
          runBody.company_name = ps.companyName;
          if (ps.companyUrl) runBody.company_url = ps.companyUrl;
          if (ps.titlesUnion?.length) runBody.titles = ps.titlesUnion;
          if (signoffTarget?.url) runBody.target_url = signoffTarget.url;
        }
        const rRes = await fetch(`${API}/sourcing-batches/${bid}/apify-runs`, {
          method: "POST",
          headers,
          body: JSON.stringify(runBody),
        });
        const rData = await rRes.json();
        if (!rRes.ok) throw new Error(rData.error || `HTTP ${rRes.status}`);
        const terminal = await pollRun(rData.id);
        results.push({
          totalElements: terminal.total_elements ?? null,
          error: terminal.status === "failed" ? terminal.error || "Run failed" : null,
        });
      } catch (e) {
        results.push({ totalElements: null, error: e.message });
      }
      setProbeLive(results.slice()); // UI-only progress
    }

    setProbeResults(results);
    const enabledArr = new Array(results.length).fill(true);
    setGroupEnabled(enabledArr);

    if (angle === "former") {
      // Build chain and run unique probes in parallel.
      const builtChain = buildChain(snap.map((ps, i) => ({
        index: i,
        titles: ps.pastJobTitles || [],
        count: (results[i] || {}).totalElements ?? 0,
        enabled: true,
      })));
      setProbingUnique(true);
      const uCounts = new Array(builtChain.length).fill(null);
      await Promise.allSettled(builtChain.map(async (cs, k) => {
        // Single-group search with no exclusion: skip probe, reuse group count.
        if (cs.groups.length === 1 && cs.exclude.length === 0) {
          uCounts[k] = (results[cs.groups[0]] || {}).totalElements ?? 0;
          return;
        }
        try {
          const uBody = {
            max_items: 1,
            past_job_titles: cs.titles,
            exclude_hr: sfExcludeHr,
            exclude_junior: sfExcludeJunior,
            meta: { kind: "unique_count", search: cs.search, groups: cs.groups },
          };
          if (cs.exclude.length > 0) uBody.exclude_past_job_titles = cs.exclude;
          if (signoffTarget?.url) uBody.company_url = signoffTarget.url;
          const rRes = await fetch(`${API}/sourcing-batches/${bid}/apify-runs`, {
            method: "POST", headers, body: JSON.stringify(uBody),
          });
          const rData = await rRes.json();
          if (!rRes.ok) throw new Error(rData.error || `HTTP ${rRes.status}`);
          const terminal = await pollRun(rData.id);
          uCounts[k] = terminal.total_elements ?? 0;
        } catch (_) {
          uCounts[k] = null;
        }
      }));
      setUniqueCounts(uCounts);
      // Seed V3 cache with initial probe results.
      const seedCache = {};
      builtChain.forEach((cs, k) => { if (uCounts[k] !== null) seedCache[makeCacheKey(cs)] = uCounts[k]; });
      setUniqueCountsCache(seedCache);
      setProbingUnique(false);
    } else {
      setFetchCaps(results.map((r) => r.totalElements != null ? Math.min(r.totalElements, 2500) : 0));
    }

    setProgressMsg("");
    setStage("counts");
  };

  // ── handleUpdateUnique ────────────────────────────────────────────────────
  const handleUpdateUnique = async () => {
    if (autoRecountTimerRef.current) { clearTimeout(autoRecountTimerRef.current); autoRecountTimerRef.current = null; }
    setUniqueUpdating(true);
    const currentChain = buildChain(probeSets.map((ps, i) => ({
      index: i,
      titles: ps.pastJobTitles || [],
      count: (probeResults[i] || {}).totalElements ?? 0,
      enabled: groupEnabled[i] ?? true,
    })));
    const uCounts = new Array(currentChain.length).fill(null);
    const newCacheEntries = {};
    await Promise.allSettled(currentChain.map(async (cs, k) => {
      if (cs.groups.length === 1 && cs.exclude.length === 0) {
        uCounts[k] = (probeResults[cs.groups[0]] || {}).totalElements ?? 0;
        newCacheEntries[makeCacheKey(cs)] = uCounts[k];
        return;
      }
      const cacheKey = makeCacheKey(cs);
      if (uniqueCountsCache[cacheKey] !== undefined) {
        uCounts[k] = uniqueCountsCache[cacheKey];
        return;
      }
      try {
        const uBody = {
          max_items: 1,
          past_job_titles: cs.titles,
          exclude_hr: sfExcludeHr,
          exclude_junior: sfExcludeJunior,
          meta: { kind: "unique_count", search: cs.search, groups: cs.groups },
        };
        if (cs.exclude.length > 0) uBody.exclude_past_job_titles = cs.exclude;
        if (signoffTarget?.url) uBody.company_url = signoffTarget.url;
        const rRes = await fetch(`${API}/sourcing-batches/${batchId}/apify-runs`, {
          method: "POST", headers, body: JSON.stringify(uBody),
        });
        const rData = await rRes.json();
        if (!rRes.ok) throw new Error(rData.error || `HTTP ${rRes.status}`);
        const terminal = await pollRun(rData.id);
        uCounts[k] = terminal.total_elements ?? 0;
        newCacheEntries[cacheKey] = uCounts[k];
      } catch (_) {
        uCounts[k] = null;
      }
    }));
    setUniqueCounts(uCounts);
    setUniqueCountsCache(prev => ({ ...prev, ...newCacheEntries }));
    setUniqueNeedsUpdate(false);
    setUniqueUpdating(false);
  };

  // ── handleBackToPlan ──────────────────────────────────────────────────────
  const handleBackToPlan = () => {
    if (autoRecountTimerRef.current) { clearTimeout(autoRecountTimerRef.current); autoRecountTimerRef.current = null; }
    setProbeSets([]);
    setProbeResults([]);
    setFetchCaps([]);
    setGroupEnabled([]);
    setUniqueCounts(null);
    setUniqueNeedsUpdate(false);
    setUniqueCountsCache({});
    setCountsExpandedGroups(new Set());
    setTightenBanner(null);
    setTightenError(null);
    setStage("signoff");
  };

  // ── handleTighten ─────────────────────────────────────────────────────────
  const handleTighten = async () => {
    setTightenError(null);
    setTightenLoading(true);
    const prevSets = sets;
    const roleGroups = validSets.map((s) => parseTitles(s.titles));
    const countPayload = probeSets.map((ps, i) => ({
      company: ps.label,
      people_found: (probeResults[i] || {}).totalElements ?? null,
    }));
    try {
      const resp = await fetch(`${API}/search-plans/tighten`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          angle,
          target_name: signoffTarget?.name || "",
          search_context: searchContext,
          role_groups: roleGroups,
          counts: countPayload,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setTightenError(data.error || `Error ${resp.status}`);
        return;
      }
      const newSets = (data.role_groups || []).map((g) => ({ titles: g.join(", ") }));
      setSets(newSets);
      const removed = data.removed || [];
      const first8 = removed.slice(0, 8).join(", ");
      const ellipsis = removed.length > 8 ? "…" : "";
      setTightenBanner({
        text: `Tightened — removed ${removed.length} title${removed.length === 1 ? "" : "s"}: ${first8}${ellipsis}`,
        prevSets,
      });
      setUniqueCountsCache({});
      setCountsExpandedGroups(new Set());
      setStage("signoff");
    } catch (e) {
      setTightenError(e.message || "Request failed");
    } finally {
      setTightenLoading(false);
    }
  };

  // ── handlePull ────────────────────────────────────────────────────────────
  const handlePull = async () => {
    setError(null);
    if (angle === "former" && uniqueCounts && uniqueCounts.some(c => c === null)) return;

    // Build per-page params; skip pages with cap 0
    let pages;
    if (angle === "former") {
      const currentChain = buildChain(probeSets.map((ps, i) => ({
        index: i,
        titles: ps.pastJobTitles || [],
        count: (probeResults[i] || {}).totalElements ?? 0,
        enabled: groupEnabled[i] ?? true,
      })));
      pages = currentChain.map((cs, k) => {
        const maxItems = roundMaxItems((uniqueCounts && uniqueCounts[k]) ?? 0);
        if (!maxItems) return null;
        const page = {
          max_items: maxItems,
          past_job_titles: cs.titles,
          exclude_hr: sfExcludeHr,
          exclude_junior: sfExcludeJunior,
          meta: { kind: "pull", search: cs.search, of: currentChain.length, groups: cs.groups },
        };
        if (cs.exclude.length > 0) page.exclude_past_job_titles = cs.exclude;
        if (signoffTarget?.url) page.company_url = signoffTarget.url;
        return page;
      }).filter(Boolean);
    } else {
      pages = probeSets.map((ps, i) => {
        const cap = Number(fetchCaps[i]);
        if (!cap) return null;
        const page = { max_items: Math.min(2500, cap), exclude_hr: sfExcludeHr, exclude_junior: sfExcludeJunior };
        page.company_name = ps.companyName;
        if (ps.companyUrl) page.company_url = ps.companyUrl;
        if (ps.titlesUnion?.length) page.titles = ps.titlesUnion;
        if (signoffTarget?.url) page.target_url = signoffTarget.url;
        return page;
      }).filter(Boolean);
    }

    // Pre-check credits before creating any rows
    try {
      const cr = await fetch(`${API}/credits/me`, { headers });
      if (cr.ok) {
        const crData = await cr.json();
        if (typeof crData.balance === 'number') {
          setCreditsMe(crData);
          const cpp = crData.credits_per_profile ?? 1;
          const needed = totalFetch * cpp;
          if (crData.balance < needed) {
            return;
          }
        }
      }
    } catch (_) {}

    setStage("pulling");
    setPullResults([]);
    setPullRuns([]);
    setPullLive({}); // UI-only progress

    // Create all pending rows and submit first wave in one request
    let runIds;
    try {
      setProgressMsg(`Submitting ${pages.length} page${pages.length !== 1 ? "s" : ""}…`);
      const bRes = await fetch(`${API}/sourcing-batches/${batchId}/apify-runs/batch`, {
        method: "POST",
        headers,
        body: JSON.stringify({ pages }),
      });
      window.dispatchEvent(new Event('rasp:credits-changed'));
      const bData = await bRes.json();
      if (bRes.status === 402) {
        setCreditsMe((prev) => prev ? { ...prev, balance: bData.available ?? prev.balance } : prev);
        setStage("counts");
        return;
      }
      if (!bRes.ok) throw new Error(bData.error || `HTTP ${bRes.status}`);
      runIds = bData.run_ids;
    } catch (e) {
      setError(e.message);
      setStage("done");
      return;
    }

    // Fetch run data for labels (start_page, companies, max_items); order by runIds position
    try {
      const rRes = await fetch(`${API}/sourcing-batches/${batchId}/apify-runs`, { headers });
      if (rRes.ok) {
        const runsData = await rRes.json();
        const runMap = {};
        runsData.forEach((r) => { runMap[r.id] = r; });
        setPullRuns(runIds.map((id) => runMap[id] || null));
      }
    } catch (_) {}

    // Poll all runs concurrently; collect results in page order
    setProgressMsg(`Waiting for ${runIds.length} page${runIds.length !== 1 ? "s" : ""}…`);
    const resultArr = new Array(runIds.length).fill(null).map(() => ({ staged: 0, dupes: 0, error: null }));
    await Promise.allSettled(
      runIds.map(async (runId, i) => {
        try {
          const terminal = await pollRun(runId);
          resultArr[i] = {
            staged: terminal.staged ?? 0,
            dupes: terminal.intra_batch_dupes ?? 0,
            error: (terminal.status === "failed") ? terminal.error || "Run failed" : null,
          };
        } catch (e) {
          resultArr[i] = { staged: 0, dupes: 0, error: e.message };
        }
        setPullLive((p) => ({ ...p, [i]: resultArr[i] })); // UI-only progress
      })
    );

    setPullResults(resultArr);
    setProgressMsg("");
    setStage("done");
    if (onIngested) onIngested();
  };

  // ── Presentation (Source flow D) ──────────────────────────────────────────
  // Everything below reads existing state; the stage machine above is unchanged.
  // `shownStage` lags `stage` by COMPLETE_HOLD_MS after an async stage succeeds.
  const step = STEP_OF[shownStage];
  const elapsed = Math.max(0, now - stageStartRef.current);
  const doneCancelled = shownStage === "done" && pullResults.length === 0;
  const [stageTitle, stageSub] = doneCancelled
    ? ["Search stopped", "Your probe leads are saved in the batch below."]
    : STAGE_COPY[shownStage];
  const panelBorder = busy
    ? "rgba(45,212,191,.35)"
    : shownStage === "done"
    ? "rgba(69,196,154,.35)"
    : "var(--line)";
  const batchLabel = `${ANGLE_LABELS[angle]} — ${signoffTarget?.name || ""}`;
  const expectProposing = (angle === 'competitor' || angle === 'customer') ? 45000 : EXPECT_MS.proposing;
  const unit = angle === "former" ? "group" : "company";
  const units = angle === "former" ? "groups" : "companies";
  const secsLeft = (ms) => `About ${Math.max(1, Math.ceil(ms / 1000))}s left`;
  const proposedTitleStrings = new Set(
    ((proposedPlan && proposedPlan.plan && proposedPlan.plan.title_sets) || []).map((ts) => ts.join(", "))
  );

  // ── Loading screen data (proposing / probing / pulling) ──
  const loading = (() => {
    if (shownStage === "proposing") {
      const idx = holding ? 4 : Math.min(3, Math.floor(elapsed / (expectProposing / 4)));
      const plan = holding && proposedPlan ? proposedPlan.plan : null;
      const words = wordCount(contextInput);
      const titleCount = plan
        ? new Set((plan.title_sets || []).flat().map((t) => String(t).toLowerCase())).size
        : null;
      const groupCount = plan
        ? (angle === "former" ? (plan.title_sets || []).length : (plan.company_sets || []).length)
        : null;
      const linked = plan ? !!(plan.target && plan.target.url) : null;
      const defs = [
        ["Read your topic", words ? plural(words, "word") : "no topic"],
        ["Find the roles that matter", plan ? plural(titleCount, "title") : ""],
        angle === "former"
          ? ["Group job titles", plan ? plural(groupCount, "group") : ""]
          : ["Pick the companies to search", plan ? plural(groupCount, "company", "companies") : ""],
        ["Check the company link", plan ? (linked ? "linked" : "name match") : ""],
      ];
      const company = targetChip ? targetChip.name : "the company";
      const lines = [
        words ? "Reading what you wrote…" : `Looking at ${company}…`,
        `Figuring out which roles matter at ${company}…`,
        angle === "former" ? "Grouping job titles into sets…" : "Picking the companies to search…",
        "Checking the company link…",
      ];
      const left = expectProposing - elapsed;
      return {
        title: `Putting together a plan for ${company}`,
        status: holding ? "Your plan is ready" : lines[idx],
        reassurance: "Nothing is being fetched yet — you get to review this first.",
        progress: holding ? 1 : Math.min(0.92, elapsed / expectProposing),
        rows: defs.map(([label, value], k) => ({
          label,
          state: k < idx ? "done" : k === idx ? "active" : "todo",
          value: k < idx ? value : "",
          valueColor: value === "name match" ? "var(--warning)" : undefined,
        })),
        eta: holding ? "Done" : left > 0 ? secsLeft(left) : "Almost there…",
      };
    }
    if (shownStage === "probing") {
      const n = probeSets.length;
      const doneN = holding ? n : probeLive.length;
      const cur = holding || doneN >= n ? 0 : Math.min(0.9, (now - probeStepStartRef.current) / EXPECT_MS.probeSet);
      const left = (n - doneN) * EXPECT_MS.probeSet - (now - probeStepStartRef.current);
      const uniqueActive = angle === 'former' && doneN >= n && !holding && uniqueCounts === null;
      const uniqueDoneForLoading = angle === 'former' && (uniqueCounts !== null || holding);
      const totalSteps = angle === 'former' ? n + 1 : n;
      const uniqueProgress = angle === 'former' ? (uniqueDoneForLoading ? 1 : (uniqueActive ? 0.5 : 0)) : 0;
      const groupRows = probeSets.map((ps, i) => {
        const r = probeLive[i] || (holding ? probeResults[i] : null);
        return {
          label: ps.pastJobTitles ? ps.pastJobTitles.join(", ") : ps.label,
          state: r ? "done" : i === doneN ? "active" : "todo",
          value: r ? (r.error ? "error" : fmtN(r.totalElements)) : "",
          valueColor: r && r.error ? "var(--negative)" : undefined,
        };
      });
      const rows = angle === 'former'
        ? [...groupRows, {
            label: "Counting unique people…",
            state: uniqueDoneForLoading ? "done" : (doneN >= n ? "active" : "todo"),
            value: "",
          }]
        : groupRows;
      return {
        title: "Checking how many people match",
        status: uniqueActive
          ? "Counting unique people…"
          : doneN >= n ? `All ${units} counted` : `Counting ${unit} ${doneN + 1} of ${n}…`,
        reassurance: `We look at one profile per ${unit} — just enough to count.`,
        progress: totalSteps ? (doneN + cur + uniqueProgress) / totalSteps : 0,
        rows,
        eta: doneN >= n ? "Done" : left > 0 ? secsLeft(left) : "Almost there…",
      };
    }
    if (shownStage === "pulling") {
      const formerPages = (angle === 'former' && chain && uniqueCounts)
        ? chain.filter((cs, k) => roundMaxItems(uniqueCounts[k] ?? 0) > 0)
        : null;
      const n = formerPages ? formerPages.length : probeSets.length;
      const live = { ...pullLive };
      if (holding) pullResults.forEach((r, i) => { if (!live[i]) live[i] = r; });
      const doneN = Object.keys(live).length;
      const inflight = Math.min(0.9, elapsed / EXPECT_MS.pullRun);
      const staged = Object.values(live).reduce((s, r) => s + (r.staged || 0), 0);
      const left = EXPECT_MS.pullRun - elapsed;
      const starting = doneN === 0 && progressMsg.startsWith("Submitting");
      return {
        title: "Bringing in your leads",
        status: starting ? `Starting ${plural(n, "run")}…` : `${fmtN(staged)} leads in so far`,
        reassurance: "Feel free to leave this page — the batch keeps filling in the background.",
        progress: holding ? 1 : n ? (doneN + (n - doneN) * inflight) / n : 0,
        rows: formerPages
          ? formerPages.map((cs, k) => {
              const r = live[k];
              const label = `Search ${cs.search} · groups ${cs.groups.map(g => g + 1).join(', ')}`;
              return {
                label,
                state: r ? "done" : "active",
                value: r ? (r.error ? "failed" : fmtN(r.staged)) : "",
                valueColor: r && r.error ? "var(--negative)" : undefined,
              };
            })
          : probeSets.map((ps, i) => {
              const r = live[i];
              return {
                label: ps.pastJobTitles ? ps.pastJobTitles.join(", ") : ps.label,
                state: r ? "done" : "active",
                value: r ? (r.error ? "failed" : fmtN(r.staged)) : "",
                valueColor: r && r.error ? "var(--negative)" : undefined,
              };
            }),
        eta: holding || doneN >= n
          ? "Done"
          : left > 0
          ? secsLeft(left)
          : elapsed > EXPECT_MS.pullRun * 2
          ? "Taking longer than usual — still running"
          : "Almost there…",
      };
    }
    return null;
  })();

  // ── Counts data ──
  const counts = probeSets.map((_, i) => (probeResults[i] || {}).totalElements);
  const totalMatch = counts.reduce((s, c) => s + (c || 0), 0);
  const maxCount = Math.max(1, ...counts.map((c) => c || 0));
  const failedCounts = probeResults.filter((r) => r && r.error).length;
  const setCap = (i, v) => setFetchCaps((p) => p.map((x, j) => (j === i ? v : x)));

  // ── Shared styles ──
  const eyebrow = { fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 500, letterSpacing: ".08em", color: "var(--text-4)", textTransform: "uppercase" };
  const checkLabel = { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--text-2)", cursor: "pointer", whiteSpace: "nowrap" };
  const checkInput = { accentColor: "var(--accent)", width: 14, height: 14, margin: 0 };
  const bodyStyle = { display: "flex", flexWrap: "wrap", gap: "18px 28px", padding: "22px 28px 28px", animation: "nsp-slideIn .4s cubic-bezier(.2,.7,.2,1)" };
  const mainCol = { flex: "1 1 420px", minWidth: 0, display: "flex", flexDirection: "column", gap: 14 };
  const primaryBtn = { height: 34, padding: "0 18px", fontWeight: 600 };
  const banner = (tone) => ({
    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, fontSize: 12, color: "var(--text-2)",
    border: tone === "green" ? "1px solid rgba(69,196,154,.3)" : "1px solid rgba(45,212,191,.3)",
    background: tone === "green" ? "rgba(69,196,154,.07)" : "linear-gradient(90deg, rgba(14,116,144,.30), rgba(45,212,191,.06))",
  });
  const bannerDot = (c) => <span style={{ width: 6, height: 6, borderRadius: "50%", background: c, display: "block", flex: "none" }} />;

  const excludeChecks = (hr, setHr, jr, setJr) => (
    <>
      <label style={checkLabel}>
        <input type="checkbox" checked={hr} onChange={(e) => setHr(e.target.checked)} style={checkInput} />
        Exclude HR / recruiting
      </label>
      <label style={checkLabel}>
        <input type="checkbox" checked={jr} onChange={(e) => setJr(e.target.checked)} style={checkInput} />
        Exclude entry-level
      </label>
    </>
  );

  const proposeHint = !targetChip
    ? "Add a company to continue"
    : angle === "customer" && !contextInput.trim()
    ? "Describe who you're looking for to continue"
    : "Nothing is fetched yet — you'll review the plan first";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section
      className="nsp-panel"
      data-stage={shownStage}
      style={{ maxWidth: shownStage === "configure" ? 960 : 820, borderColor: panelBorder }}
    >
      {/* ── Guided header ── */}
      <div style={{ padding: "24px 28px 0", display: "flex", flexDirection: "column", gap: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <StepPills step={step} />
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-4)" }}>{STAGE_META[shownStage]}</span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div key={shownStage + (doneCancelled ? "-x" : "")} style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-.015em", color: "var(--text-1)", animation: "nsp-countUp .35s ease-out" }}>
              {stageTitle}
            </div>
            <div style={{ fontSize: 14, color: "var(--text-3)", marginTop: 4, lineHeight: 1.5 }}>{stageSub}</div>
          </div>
          {shownStage === "configure" && (
            <button
              type="button"
              className="nsp-help"
              aria-expanded={showHelp}
              onClick={() => setShowHelp((v) => !v)}
            >
              <span className="nsp-help-q">?</span>
              How does this work
            </button>
          )}
        </div>
        {shownStage === "configure" && showHelp && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12,
            padding: "14px 16px", border: "1px solid var(--line-soft)", borderRadius: 10, background: "var(--subbar-bg)",
            animation: "nsp-popIn .25s ease-out",
          }}>
            {[
              ["01", "Draft a plan", "Rasp drafts the companies, job title groups and topic for you to review."],
              ["02", "Count matches", "A small check per group shows how many people match; you choose how many to bring in."],
              ["03", "Bring in and rank", "Leads land in a batch below; rank them there (and score, for formers)."],
            ].map(([num, t, desc]) => (
              <div key={num} style={{ display: "flex", gap: 8 }}>
                <span style={{ fontFamily: "var(--font-data)", fontSize: 11, color: "var(--accent)", flexShrink: 0, marginTop: 1 }}>{num}</span>
                <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5 }}>
                  <strong style={{ color: "var(--text-1)", fontWeight: 500 }}>{t}.</strong> {desc}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ height: 1, background: "rgba(130,210,220,.10)", margin: "18px 28px 0" }} />

      {error && !busy && (
        <div style={{
          margin: "16px 28px 0", padding: "8px 12px", borderRadius: 8, fontSize: 12, color: "var(--negative)",
          border: "1px solid rgba(224,106,90,.32)", background: "rgba(224,106,90,.08)", display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ fontSize: 11, background: "none", border: "none", color: "var(--negative)", cursor: "pointer", textDecoration: "underline" }}
          >
            dismiss
          </button>
        </div>
      )}

      {/* ── CONFIGURE ── */}
      {shownStage === "configure" && (
        <div style={{ ...bodyStyle, flexDirection: "column", flexWrap: "nowrap", gap: 18 }}>
          {/* Who */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span className="nsp-label">Who are you looking for?</span>
            <div className="nsp-seg">
              {["former", "competitor", "customer"].map((a) => (
                <button key={a} type="button" onClick={() => setAngle(a)} className={angle === a ? "nsp-seg-on" : ""}>
                  {ANGLE_LABELS[a]}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 12, color: "var(--text-4)" }}>{ANGLE_HINT}</span>
          </div>

          {/* Company + titles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
              <span className="nsp-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Which company?
                {targetChip && <span data-testid="nsp-target-dot" style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--positive)", display: "block" }} />}
              </span>
              <div
                className="nsp-field"
                style={{ minHeight: 40, padding: "3px 6px 3px 12px", gap: 6, flexWrap: "wrap", ...(targetChip ? { borderColor: "rgba(45,212,191,.3)" } : {}) }}
              >
                {targetChip ? (
                  <Chip
                    tone="committed"
                    chip={targetChip}
                    onUpdate={(c) => setTargetChip(c)}
                    onRemove={() => setTargetChip(null)}
                    onClear={() => setTargetChip(null)}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="Type a company and press Enter"
                    value={targetInput}
                    onChange={(e) => setTargetInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTargetChip(); } }}
                    style={{ height: 32, minWidth: 120 }}
                  />
                )}
              </div>
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
              <span className="nsp-label">
                {angle === "former"
                  ? "Any job titles in mind?"
                  : angle === "competitor"
                  ? "Any current role titles in mind?"
                  : "Any likely roles in mind?"}{" "}
                <span className="nsp-hint">Optional</span>
              </span>
              <span className="nsp-field" style={{ height: 40, padding: "0 12px" }}>
                <input
                  type="text"
                  placeholder="e.g. Software Engineer, Senior SWE"
                  value={titlesInput}
                  onChange={(e) => setTitlesInput(e.target.value)}
                />
              </span>
            </label>
          </div>

          {/* Companies group (competitor / customer) */}
          {angle !== "former" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span className="nsp-label">
                <span>{angle === "competitor" ? "Competitor companies" : "Customer companies"}</span>
                {angle === "customer" && (
                  <span className="nsp-hint"> Optional — leave empty and the plan will propose candidates</span>
                )}
              </span>
              <div className="nsp-field" style={{ minHeight: 40, padding: "3px 6px 3px 8px", gap: 6, flexWrap: "wrap" }}>
                {companyChips.map((c, i) => (
                  <Chip
                    key={i}
                    chip={c}
                    onUpdate={(nc) => setCompanyChips((p) => p.map((x, j) => j === i ? nc : x))}
                    onRemove={() => setCompanyChips((p) => p.filter((_, j) => j !== i))}
                    warnMixed={anyConfigCompanyLinked && !c.url}
                  />
                ))}
                {companyChips.length < 10 && (
                  <input
                    type="text"
                    placeholder="Type a company and press Enter"
                    value={companyInput}
                    onChange={(e) => setCompanyInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCompanyChip(); } }}
                    style={{ height: 32, minWidth: 160, paddingLeft: 4 }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Context */}
          <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <span className="nsp-label" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <span>
                {angle === "customer" ? "Who are you looking for?" : "What should they be able to speak to?"}{" "}
                <span className="nsp-hint">
                  {angle === "customer" ? "Required" : "Optional, but it makes the ranking much better"}
                </span>
              </span>
              <span style={{ fontFamily: "var(--font-data)", fontSize: 11, fontWeight: 400, color: "var(--text-5)" }}>
                {contextInput.length ? `${contextInput.length} chars` : ""}
              </span>
            </span>
            <textarea
              className="nsp-area"
              rows={3}
              placeholder="Describe the business questions in a sentence or two"
              value={contextInput}
              onChange={(e) => setContextInput(e.target.value)}
            />
          </label>

          {/* Footer */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px 18px", flexWrap: "wrap" }}>
            {excludeChecks(excludeHr, setExcludeHr, excludeJunior, setExcludeJunior)}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: "var(--text-4)" }}>{proposeHint}</span>
            <button className="r-btn r-btn-amber nsp-draft" onClick={handlePropose} disabled={!canPropose}>
              <span className="nsp-draft-dot" />
              Draft my plan
              <span className="nsp-draft-chev" />
            </button>
          </div>
        </div>
      )}

      {/* ── PROPOSING / PROBING / PULLING ── */}
      {loading && <LoadingScreen key={shownStage} {...loading} />}

      {/* ── SIGNOFF ── */}
      {shownStage === "signoff" && (
        <div style={bodyStyle}>
          <div style={mainCol}>
            <div style={banner("teal")}>
              {bannerDot("var(--accent)")}
              <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                {angle === "former" ? (
                  <>Here's the plan. We grouped the job titles into <Mono>{validSets.length}</Mono> {validSets.length === 1 ? "set" : "sets"} — edit or remove any of them, then we'll count how many people match.</>
                ) : (
                  <>Here's the plan. We'll search <Mono>{activeCompanies.length}</Mono> {angle === "competitor" ? "competitor" : "likely customer"} {activeCompanies.length === 1 ? "company" : "companies"} — edit anything, then we'll count how many people match.</>
                )}
              </span>
            </div>

            {tightenBanner && (
              <div data-testid="tighten-banner" style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "var(--text-2)", background: "rgba(69,196,154,.08)", border: "1px solid rgba(69,196,154,.3)", borderRadius: 8, padding: "8px 12px" }}>
                <span style={{ flex: 1, minWidth: 0 }}>{tightenBanner.text}</span>
                <button
                  data-testid="tighten-undo"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontSize: 12, padding: 0, whiteSpace: "nowrap" }}
                  onClick={() => { setSets(tightenBanner.prevSets); setTightenBanner(null); }}
                >
                  Undo
                </button>
              </div>
            )}

            {signoffTarget && !signoffTarget.url && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--warning)" }}>
                {bannerDot("var(--warning)")}
                We matched the company by name. Link its LinkedIn page for an exact match (click the chip).
              </div>
            )}
            {planWarnings.length > 0 && (
              <div>
                {planWarnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 11, color: "var(--warning)", marginBottom: 2 }}>{w}</div>
                ))}
              </div>
            )}

            {/* Target chip */}
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={eyebrow}>Company</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {signoffTarget && (
                  <Chip
                    tone="target"
                    chip={signoffTarget}
                    onUpdate={(c) => setSignoffTarget(c)}
                    onRemove={() => setSignoffTarget(null)}
                  />
                )}
              </div>
              {signoffTarget && !signoffTarget.url && (
                <span data-testid="nsp-link-hint" style={{ fontSize: 11, color: "var(--warning)" }}>
                  LinkedIn link required
                </span>
              )}
            </div>

            {/* Company chips (competitor / customer) */}
            {angle !== "former" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={eyebrow}>{angle === "competitor" ? "Competitor companies" : "Customer companies"}</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
                  {signoffCompanies.map((c, i) => (
                    <React.Fragment key={i}>
                      <Chip
                        chip={c}
                        proposed={c.proposed}
                        struck={c.struck}
                        warnMixed={anySignoffCompanyLinked && !c.url && !c.struck}
                        onStrike={() =>
                          setSignoffCompanies((p) => p.map((x, j) => j === i ? { ...x, struck: !x.struck } : x))
                        }
                        onUpdate={(nc) =>
                          setSignoffCompanies((p) => p.map((x, j) => j === i ? { ...x, name: nc.name, url: nc.url } : x))
                        }
                        onRemove={() =>
                          setSignoffCompanies((p) => p.filter((_, j) => j !== i))
                        }
                      />
                      {!c.url && !c.struck && (
                        <span data-testid="nsp-link-hint" style={{ fontSize: 11, color: "var(--warning)" }}>
                          LinkedIn link required
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                  {signoffCompanies.filter((c) => !c.struck).length < 10 && (
                    <span className="nsp-field" style={{ height: 30, padding: "0 10px", flex: "1 1 160px", minWidth: 160 }}>
                      <input
                        type="text"
                        placeholder="Add company — press Enter"
                        value={sfCompanyInput}
                        onChange={(e) => setSfCompanyInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSfCompanyChip(); } }}
                        style={{ fontSize: 12 }}
                      />
                    </span>
                  )}
                </div>
                {nameMatchCompanies.length > 0 && (
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                    Matching by name (may include same-named companies):{" "}
                    {nameMatchCompanies.map((c) => c.name).join(", ")}.
                  </div>
                )}
                {addDupWarning && (
                  <div data-testid="nsp-dup-warning" style={{ fontSize: 11, color: "var(--warning)" }}>{addDupWarning}</div>
                )}
              </div>
            )}

            {/* Title groups */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ ...eyebrow, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  {angle === "former" ? "Job title groups" : angle === "competitor" ? "Current role groups" : "Likely roles"}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 10, letterSpacing: 0 }}>
                  <button
                    type="button"
                    data-testid="nsp-expand-all"
                    onClick={() => {
                      const allExp = sets.every((_, j) => expandedGroups.has(j));
                      setExpandedGroups(allExp ? new Set() : new Set(sets.map((_, j) => j)));
                    }}
                    style={{ fontSize: 10, background: "none", border: "none", cursor: "pointer", color: "var(--accent)", textDecoration: "underline", padding: 0, fontWeight: 500, letterSpacing: 0, textTransform: "none" }}
                  >
                    {sets.every((_, j) => expandedGroups.has(j)) ? "Collapse all" : "Expand all"}
                  </button>
                  <span style={{ color: "var(--text-5)" }}>{sets.length} of {maxSets}</span>
                </span>
              </span>
              {sets.map((s, i) => {
                const isProposed = proposedTitleStrings.has(s.titles);
                const isExpanded = expandedGroups.has(i);
                const toggleExpand = () => setExpandedGroups(eg => {
                  const n = new Set(eg);
                  n.has(i) ? n.delete(i) : n.add(i);
                  return n;
                });
                const groupSeniorityTitles = seniorityOnlyByGroup[i] || [];
                return (
                  <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4, animation: `nsp-fadeUp .4s ease-out ${i * 0.07}s both` }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <span style={{ fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 500, color: "var(--accent)", width: 22, flex: "none", paddingTop: 8 }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="nsp-field" style={{ flex: 1, minHeight: 34, padding: "0 10px", gap: 8, borderRadius: 8, alignItems: "flex-start", paddingTop: isExpanded ? 8 : 0, position: "relative" }}>
                        {isExpanded ? (
                          <textarea
                            data-testid={`role-group-textarea-${i}`}
                            ref={(el) => { if (el) autoSize(el); }}
                            placeholder="comma-separated titles"
                            value={s.titles}
                            onChange={(e) => {
                              setSets((p) => p.map((x, j) => j === i ? { ...x, titles: e.target.value } : x));
                              autoSize(e.target);
                            }}
                            style={{
                              fontSize: 13, width: "100%", border: "none", background: "none",
                              resize: "none", outline: "none", lineHeight: 1.5,
                              fontFamily: "inherit", color: "var(--text-1)",
                              overflow: "hidden",
                              paddingRight: isProposed ? 60 : undefined,
                            }}
                          />
                        ) : (
                          <input
                            type="text"
                            placeholder="comma-separated titles"
                            value={s.titles}
                            onChange={(e) =>
                              setSets((p) => p.map((x, j) => j === i ? { ...x, titles: e.target.value } : x))
                            }
                            style={{ fontSize: 13 }}
                          />
                        )}
                        {isProposed && (
                          <span style={{
                            fontFamily: "var(--font-data)", fontSize: 10, color: "var(--accent)", whiteSpace: "nowrap",
                            border: "1px dashed rgba(45,212,191,.4)", borderRadius: 4, padding: "1px 5px",
                            ...(isExpanded ? { position: "absolute", top: 4, right: 6, zIndex: 1 } : {}),
                          }}>proposed</span>
                        )}
                      </span>
                      <button
                        type="button"
                        data-testid={`role-group-expand-${i}`}
                        onClick={toggleExpand}
                        title={isExpanded ? "Collapse" : "Expand all titles"}
                        style={{ fontSize: 13, color: "var(--text-4)", background: "none", border: "none", padding: "8px 2px", cursor: "pointer", flex: "none" }}
                      >{isExpanded ? "⊟" : "⊞"}</button>
                      {(sets.length > 1 || angle === "customer") ? (
                        <button
                          type="button"
                          className="nsp-x"
                          style={{ marginTop: 5 }}
                          onClick={() => setSets((p) => p.filter((_, j) => j !== i))}
                          title="Remove group"
                        >×</button>
                      ) : (
                        <span style={{ width: 24, flex: "none" }} />
                      )}
                    </div>
                    {groupSeniorityTitles.map((bad) => (
                      <div
                        key={bad}
                        data-testid="nsp-seniority-warning"
                        style={{ fontSize: 12, color: "var(--warning)", paddingLeft: 30 }}
                      >
                        &ldquo;{bad}&rdquo; on its own matches every {bad} — combine it with a function, or remove it
                      </div>
                    ))}
                  </div>
                );
              })}
              {sets.length < maxSets && (
                <button
                  type="button"
                  data-testid="nsp-add-group-btn"
                  className="nsp-add"
                  onClick={() => {
                    const newIdx = sets.length;
                    setSets((p) => [...p, { titles: "" }]);
                    setExpandedGroups((prev) => new Set([...prev, newIdx]));
                  }}
                >
                  + Add another group
                </button>
              )}
            </div>

            {/* Topic */}
            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={eyebrow}>
                Your topic <span style={{ color: "var(--text-5)", textTransform: "none", letterSpacing: 0 }}>— we'll rank every lead against this</span>
              </span>
              <textarea
                className="nsp-area"
                rows={3}
                value={searchContext}
                onChange={(e) => setSearchContext(e.target.value)}
                style={{ minHeight: 76, fontSize: 13, lineHeight: 1.5, padding: "8px 10px", borderRadius: 8 }}
              />
            </label>

            {/* Footer */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px 18px", flexWrap: "wrap" }}>
              {excludeChecks(sfExcludeHr, setSfExcludeHr, sfExcludeJunior, setSfExcludeJunior)}
            </div>
            {dupCompanyPair && (
              <div data-testid="nsp-dup-pair" style={{ fontSize: 12, color: "var(--warning)" }}>
                Two companies have the same LinkedIn link: {dupCompanyPair[0]}, {dupCompanyPair[1]}.
              </div>
            )}
            {!dupCompanyPair && unlinkedCount > 0 && (
              <div data-testid="nsp-unlinked-msg" style={{ fontSize: 12, color: "var(--warning)" }}>
                Add a LinkedIn link to {unlinkedCount} {unlinkedCount === 1 ? "company" : "companies"} to continue.
              </div>
            )}
            {angle === "customer" && validSets.length === 0 && (
              <div data-testid="nsp-no-roles-msg" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "var(--warning)" }}>Add at least one likely role — Rasp only searches these roles at each customer.</span>
                {sets.length === 0 && (
                  <button
                    type="button"
                    data-testid="nsp-add-roles-btn"
                    className="r-btn r-btn-ghost"
                    style={{ fontSize: 12 }}
                    onClick={() => setSets((p) => [...p, { titles: "" }])}
                  >+ Add likely roles</button>
                )}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <button onClick={resetAll} className="r-btn r-btn-ghost" style={{ border: "none", padding: "0 6px", color: "var(--text-4)" }}>Start over</button>
              <div style={{ flex: 1 }} />
              <button onClick={() => setStage("configure")} className="r-btn r-btn-ghost">Back</button>
              <button
                className="r-btn r-btn-primary"
                onClick={handleProbe}
                disabled={!canCheckCounts}
                style={{ ...primaryBtn, ...(!canCheckCounts ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
              >
                Looks good, count matches
              </button>
            </div>
          </div>

          <SideNote sections={[
            ["Why these sets", (
              <>
                {(proposedPlan && proposedPlan.plan && proposedPlan.plan.notes) ? proposedPlan.plan.notes : (
                  <>Each {unit} is counted on its own, so a narrow {unit} gives a clean count. <Mono>{runsCount}</Mono> {runsCount === 1 ? "run" : "runs"} total.</>
                )}
                {planSourceCompanies.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      data-testid="nsp-sources-btn"
                      style={{ fontSize: 11, padding: "2px 8px", border: "1px solid var(--line)", borderRadius: 4, background: "transparent", color: "var(--text-3)", cursor: "pointer" }}
                      onClick={() => setSourcesOpen((o) => !o)}
                    >
                      Sources
                    </button>
                    {sourcesOpen && (
                      <ul data-testid="nsp-sources-list" style={{ margin: "6px 0 0", padding: "0 0 0 14px", listStyle: "disc" }}>
                        {planSourceCompanies.map((c, i) => (
                          <li key={i} style={{ marginBottom: 3 }}>
                            {c.name}{" — "}
                            <a href={c.source_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>
                              {extractDomain(c.source_url)}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )],
            ["Next", `We look at one profile per ${unit} to see how many people match. You choose how many to bring in before anything is pulled.`],
          ]} />
        </div>
      )}

      {/* ── COUNTS ── */}
      {shownStage === "counts" && (
        <div style={bodyStyle}>
          <div style={mainCol}>
            {angle === "former" ? (
              <>
                {allGroupsOff ? (
                  <div data-testid="all-groups-off-msg" style={{ fontSize: 13, color: "var(--text-4)", padding: "6px 0" }}>
                    Switch on at least one group.
                  </div>
                ) : (
                  <>
                    <div style={banner("green")}>
                      {bannerDot("var(--positive)")}
                      <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                        <span data-testid="unique-total">{uniqueNeedsUpdate ? "—" : (uniqueTotal != null ? fmtN(uniqueTotal) : "—")}</span>
                        {uniqueNeedsUpdate ? " Updating…" : " unique people across your groups"}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-4)" }}>
                      Groups overlap; the unique number is what's pulled and billed.
                    </div>
                    {hasUniqueError && (
                      <div data-testid="unique-error" style={{ fontSize: 12, color: "var(--warning)" }}>
                        {failedSearches.length === 1
                          ? `Couldn't count search ${failedSearches[0]}. Retry to continue.`
                          : `Couldn't count searches ${failedSearches.join(', ')}. Retry to continue.`
                        }
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div style={banner("green")}>
                {bannerDot("var(--positive)")}
                <span style={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                  <Mono>{fmtN(totalMatch)}</Mono> people match. Choose how many to bring in from each {unit} — fewer is cheaper, more gives better coverage.
                </span>
              </div>
            )}
            {failedCounts > 0 && (
              <div style={{ fontSize: 12, color: "var(--warning)" }}>
                {plural(failedCounts, unit, units)} couldn't be counted — set a number above to bring some in anyway.
              </div>
            )}

            <div style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
              <div className="nsp-count-grid" style={{ height: 32, background: "var(--subbar-bg)", ...eyebrow }}>
                <span />
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span>{angle === "former" ? "Group" : "Company"}</span>
                  {angle === "former" && probeSets.length > 0 && (
                    <button
                      type="button"
                      data-testid="counts-expand-all"
                      onClick={() => {
                        const allExp = probeSets.every((_, j) => countsExpandedGroups.has(j));
                        setCountsExpandedGroups(allExp ? new Set() : new Set(probeSets.map((_, j) => j)));
                      }}
                      style={{ fontSize: 10, background: "none", border: "none", cursor: "pointer", color: "var(--accent)", textDecoration: "underline", padding: 0, fontWeight: 500, letterSpacing: 0, textTransform: "none" }}
                    >
                      {probeSets.every((_, j) => countsExpandedGroups.has(j)) ? "Collapse all" : "Expand all"}
                    </button>
                  )}
                </span>
                <span style={{ textAlign: "right" }}>People found</span>
                <span style={{ textAlign: "right" }}>{angle === "former" ? "On" : "Bring in"}</span>
              </div>
              {probeSets.map((ps, i) => {
                const pr = probeResults[i] || {};
                const count = pr.totalElements;
                const showNudge =
                  angle !== "former" &&
                  !ps.linked &&
                  count != null &&
                  !pr.error &&
                  (count > 1000 || (medianCount > 0 && count > 3 * medianCount));
                const cap = Number(fetchCaps[i]) || 0;
                const label = angle === "former" && ps.pastJobTitles ? ps.pastJobTitles.join(", ") : ps.label;
                const isOverLimit = angle === "former" && count != null && count > 2500;
                const enabled = groupEnabled[i] ?? true;
                const isCountsExpanded = countsExpandedGroups.has(i);
                return (
                  <div key={i} className="nsp-count-grid" style={{ minHeight: 48, padding: "6px 14px", borderTop: "1px solid var(--line-soft)", ...(angle === "former" && isCountsExpanded ? { alignItems: "flex-start" } : {}) }}>
                    <span style={{ fontFamily: "var(--font-data)", fontSize: 10, fontWeight: 500, color: "var(--accent)", ...(angle === "former" && isCountsExpanded ? { paddingTop: 4 } : {}) }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, minWidth: 0 }}>
                        <span
                          data-testid={`counts-group-label-${i}`}
                          title={label}
                          style={{ fontSize: 13, color: "var(--text-1)", flex: 1, minWidth: 0,
                            ...(angle === "former" && !isCountsExpanded ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : {}) }}
                        >{label}</span>
                        {angle === "former" && (
                          <button
                            type="button"
                            data-testid={`counts-group-expand-${i}`}
                            onClick={() => setCountsExpandedGroups(eg => { const n = new Set(eg); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                            title={isCountsExpanded ? "Collapse" : "Expand"}
                            style={{ fontSize: 11, color: "var(--text-4)", background: "none", border: "none", padding: "2px", cursor: "pointer", flex: "none" }}
                          >{isCountsExpanded ? "⊟" : "⊞"}</button>
                        )}
                        {angle !== "former" && (
                          <span style={{
                            fontSize: 10, fontFamily: "var(--font-data)", padding: "1px 5px", borderRadius: 4, whiteSpace: "nowrap", flex: "none",
                            background: ps.linked ? "rgba(45,212,191,.08)" : "rgba(224,160,80,.08)",
                            color: ps.linked ? "var(--accent)" : "var(--warning)",
                            border: `1px solid ${ps.linked ? "rgba(45,212,191,.28)" : "rgba(224,160,80,.28)"}`,
                          }}>
                            {ps.linked ? "exact" : "name match"}
                          </span>
                        )}
                      </div>
                      {count != null && !pr.error && angle !== "former" && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                          <span style={{ height: 3, borderRadius: 2, background: "linear-gradient(90deg,#0e7490,#2dd4bf)", width: Math.max(2, Math.round((count / maxCount) * 120)) }} />
                          <span style={{ fontFamily: "var(--font-data)", fontSize: 10, color: "var(--text-4)" }}>
                            {totalMatch ? Math.round((count / totalMatch) * 100) : 0}%
                          </span>
                        </div>
                      )}
                      {showNudge && (
                        <div style={{ fontSize: 11, color: "var(--warning)", marginTop: 3 }}>
                          Outsized for a name match — same-name collision? Link its LinkedIn page and re-check.
                        </div>
                      )}
                      {isOverLimit && (
                        <div style={{ fontSize: 11, color: "var(--warning)", marginTop: 3 }}>
                          Matches {fmtN(count)}; LinkedIn returns at most 2,500 per search, so about {fmtN(count - 2500)} won't be reached. Tighten its titles to reach them.
                        </div>
                      )}
                    </span>
                    <span style={{
                      fontFamily: "var(--font-data)", fontSize: 13, fontWeight: 500, textAlign: "right",
                      color: pr.error ? "var(--negative)" : (count === 0 || (count == null && !pr.error)) ? "var(--warning)" : "var(--text-1)",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }} title={pr.error ? "Count failed — set a number to bring some in anyway" : undefined}>
                      {pr.error
                        ? "Count failed — set a number to bring some in anyway"
                        : (count === 0 || count == null)
                        ? (ps.companyUrl
                            ? <a href={ps.companyUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>0 found — check the LinkedIn link</a>
                            : "0 found — check the LinkedIn link"
                          )
                        : fmtN(count)}
                    </span>
                    <span style={{ display: "flex", justifyContent: "flex-end" }}>
                      {angle === "former" ? (
                        <button
                          data-testid={`group-toggle-${i}`}
                          type="button"
                          onClick={() => {
                            const base = groupEnabled.length > 0 ? groupEnabled : new Array(probeSets.length).fill(true);
                            const next = base.map((v, j) => j === i ? !v : v);
                            setGroupEnabled(next);
                            const anyEnabled = next.some(Boolean);
                            if (autoRecountTimerRef.current) clearTimeout(autoRecountTimerRef.current);
                            if (!anyEnabled) {
                              autoRecountTimerRef.current = null;
                              setUniqueNeedsUpdate(false);
                            } else {
                              setUniqueNeedsUpdate(true);
                              autoRecountTimerRef.current = setTimeout(() => {
                                autoRecountTimerRef.current = null;
                                setAutoRecountSignal(s => s + 1);
                              }, 1200);
                            }
                          }}
                          style={{
                            fontSize: 12, fontFamily: "var(--font-data)", padding: "2px 10px", borderRadius: 6, cursor: "pointer",
                            background: enabled ? "rgba(45,212,191,.12)" : "rgba(255,255,255,.05)",
                            color: enabled ? "var(--accent)" : "var(--text-4)",
                            border: `1px solid ${enabled ? "rgba(45,212,191,.3)" : "var(--line)"}`,
                          }}
                        >
                          {enabled ? "On" : "Off"}
                        </button>
                      ) : (
                        <span className="nsp-stepper">
                          <button
                            type="button"
                            aria-label="Bring in fewer"
                            onClick={() => setCap(i, Math.max(0, Math.min(cap, Math.max(0, cap - 100))))}
                          >−</button>
                          <input
                            type="number"
                            min={0}
                            max={2500}
                            value={fetchCaps[i] ?? ""}
                            onChange={(e) =>
                              setFetchCaps((p) => p.map((v, j) => j === i ? Number(e.target.value) : v))
                            }
                            title="Bring in"
                          />
                          <button
                            type="button"
                            aria-label="Bring in more"
                            onClick={() => setCap(i, Math.min(2500, cap + 100))}
                          >+</button>
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            {totalMatch > 5000 && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(255,190,50,.07)", border: "1px solid rgba(255,190,50,.3)", fontSize: 12, color: "var(--text-2)" }}>
                <span style={{ flex: 1 }}>Lots of matches — Rasp can trim the roles to the most specific titles.</span>
                <button
                  data-testid="tighten-btn"
                  className="r-btn r-btn-ghost"
                  onClick={handleTighten}
                  disabled={tightenLoading}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {tightenLoading ? "Tightening…" : "Tighten search"}
                </button>
              </div>
            )}
            {tightenError && (
              <div data-testid="tighten-error" style={{ fontSize: 12, color: "var(--warning)" }}>{tightenError}</div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "12px 18px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 18 }}>
                <div>
                  <div style={{ ...eyebrow, textTransform: "none" }}>YOU'LL GET</div>
                  <div style={{ fontFamily: "var(--font-data)", fontSize: 18, fontWeight: 500, lineHeight: 1.2, color: "var(--text-1)" }}>
                    {isFormerStale ? "—" : fmtN(totalFetch)}
                  </div>
                </div>
                {creditsMe && (() => {
                  const cpp = creditsMe.credits_per_profile ?? 1;
                  const needed = totalFetch * cpp;
                  const have = creditsMe.balance ?? 0;
                  const creditShort = !isFormerStale && have < needed;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span data-testid="credits-preview" style={{ fontSize: 13, color: "var(--text-2)", fontVariantNumeric: "tabular-nums" }}>
                        {isFormerStale ? "—" : `Up to ${needed.toLocaleString('en-US')} credits · you have ${have.toLocaleString('en-US')}`}
                      </span>
                      {creditShort && (
                        <span data-testid="credits-short" style={{ fontSize: 13, color: "var(--red, #f87171)" }}>
                          Not enough credits. Ask your admin for more.
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{ flex: 1 }} />
              {angle === "former" && hasUniqueError && (
                <button
                  data-testid="update-unique"
                  className="r-btn r-btn-ghost"
                  onClick={handleUpdateUnique}
                  disabled={uniqueUpdating}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {uniqueUpdating ? "Updating…" : "Retry"}
                </button>
              )}
              <button data-testid="back-to-plan-btn" onClick={handleBackToPlan} className="r-btn r-btn-ghost">Back to plan</button>
              {creditsMe && (() => {
                const cpp = creditsMe.credits_per_profile ?? 1;
                const needed = totalFetch * cpp;
                const have = creditsMe.balance ?? 0;
                const creditShort = have < needed;
                const isDisabled = isFormerStale || totalFetch === 0 || creditShort;
                return (
                  <button
                    className="r-btn r-btn-primary"
                    onClick={handlePull}
                    disabled={isDisabled}
                    style={{ ...primaryBtn, ...(isDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                  >
                    {isFormerStale ? "Bring in leads" : <>Bring in <span style={{ fontFamily: "var(--font-data)", fontSize: 12, fontWeight: 500 }}>{fmtN(totalFetch)}</span> leads</>}
                  </button>
                );
              })()}
              {!creditsMe && (
                <button
                  className="r-btn r-btn-primary"
                  onClick={handlePull}
                  disabled={isFormerStale || totalFetch === 0}
                  style={{ ...primaryBtn, ...((isFormerStale || totalFetch === 0) ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
                >
                  {isFormerStale ? "Bring in leads" : <>Bring in <span style={{ fontFamily: "var(--font-data)", fontSize: 12, fontWeight: 500 }}>{fmtN(totalFetch)}</span> leads</>}
                </button>
              )}
            </div>
          </div>

          <SideNote sections={angle === "former" ? [
            ["Reading the counts", "Each group’s count is how many people match its titles. Groups overlap, so the counts add up to more than the unique total."],
            [null, "Switch a group off to leave its titles out. Rasp pulls every unique person across the groups that are on: each person once, 1 credit each."],
            [null, "LinkedIn shows at most 2,500 people per search. If a group is over that, tighten its titles to reach everyone."],
            [null, <><button data-testid="stop-here-link" onClick={() => setStage("done")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontSize: "inherit", padding: 0 }}>Stop here</button> keeps the probe leads in the batch so nothing is lost.</>],
          ] : [
            ["Reading the counts", <>Caps start at the full match (max <Mono>2,500</Mono>). Lower a cap to reduce the credits used — 1 credit per profile.</>],
            [null, "Caps take the first N in the vendor's search order, not the best N — ranking happens after the leads are in."],
            [null, <><button data-testid="stop-here-link" onClick={() => setStage("done")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontSize: "inherit", padding: 0 }}>Stop here</button> keeps the probe leads in the batch so nothing is lost.</>],
          ]} />
        </div>
      )}

      {/* ── DONE ── */}
      {shownStage === "done" && (
        <div style={bodyStyle}>
          <div style={{ ...mainCol, gap: 16 }}>
            {rankStatus && rankStatus.batchId === batchId && rankStatus.state === 'ranking' ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div>
                    <div data-testid="auto-rank-progress" style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em", color: "var(--text-1)" }}>
                      {rankStatus.msg || "Ranking your leads…"}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-3)" }}>Ranking to the top — just a moment.</div>
                  </div>
                </div>
              </>
            ) : rankStatus && rankStatus.batchId === batchId && rankStatus.state === 'error' ? (
              <>
                <div data-testid="auto-rank-error" style={{ fontSize: 13, color: "var(--negative)" }}>
                  {rankStatus.msg || "Ranking failed"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button
                    data-testid="auto-rank-retry"
                    className="r-btn r-btn-primary"
                    onClick={() => { autoRankCalledRef.current = false; onOpenBatch(batchId, { rank: true }); }}
                    style={primaryBtn}
                  >
                    Try again
                  </button>
                  <button onClick={resetAll} className="r-btn r-btn-ghost">Start another search</button>
                </div>
              </>
            ) : pullResults.length > 0 ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span style={{
                    width: 40, height: 40, borderRadius: "50%", background: "rgba(69,196,154,.12)", border: "1px solid rgba(69,196,154,.4)",
                    display: "flex", alignItems: "center", justifyContent: "center", flex: "none", animation: "nsp-popIn .4s ease-out",
                  }}>
                    <Tick color="var(--positive)" w={8} h={14} stroke={2} mt={-3} />
                  </span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em", color: "var(--text-1)" }}>All done — your leads are in</div>
                    <div style={{ fontSize: 13, color: "var(--text-3)" }}>
                      Find them under <span style={{ color: "var(--text-1)" }}>{batchLabel}</span> below. Next step: let Rasp rank them for you.
                    </div>
                  </div>
                </div>
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1, border: "1px solid var(--line)",
                  borderRadius: 8, background: "var(--line)", overflow: "hidden",
                }}>
                  {[
                    ["Staged", totalStaged, "var(--text-1)"],
                    ["Runs", pullResults.length, "var(--text-1)"],
                    ["Overlaps deduped", totalDupes, "var(--warning)"],
                  ].map(([k, v, c]) => (
                    <div key={k} style={{ padding: "14px 16px", background: "rgba(10,22,40,.6)" }}>
                      <div style={{ ...eyebrow, letterSpacing: ".1em", marginBottom: 6 }}>{k}</div>
                      <div style={{ fontFamily: "var(--font-data)", fontSize: 26, fontWeight: 500, lineHeight: 1, letterSpacing: "-.02em", color: c }}>{fmtN(v)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {pullResults.map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: r.error ? "var(--negative)" : "var(--text-3)" }}>
                      <span style={{ width: 14, display: "flex", justifyContent: "center", flex: "none" }}>
                        {r.error ? "×" : <Tick color="var(--positive)" w={4} h={8} mt={-2} />}
                      </span>
                      <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {pullRuns[i] ? buildRunLabel(pullRuns[i]) : `Run ${i + 1}`}
                      </span>
                      <span style={{ fontFamily: "var(--font-data)", fontSize: 11 }}>
                        {r.error ? r.error : <><span style={{ color: "var(--text-2)" }}>{fmtN(r.staged)}</span> staged · {fmtN(r.dupes)} dupes</>}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {onOpenBatch && batchId != null && (
                    <button
                      className="r-btn r-btn-primary"
                      onClick={() => onOpenBatch(batchId, { rank: true })}
                      style={primaryBtn}
                    >
                      Rank my leads →
                    </button>
                  )}
                  <button onClick={resetAll} className="r-btn r-btn-ghost">Start another search</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, color: "var(--text-3)" }}>
                  Search cancelled — probe leads remain in the batch{signoffTarget ? <> under <span style={{ color: "var(--text-1)" }}>{batchLabel}</span></> : null}.
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={resetAll} className="r-btn r-btn-ghost">Start another search</button>
                </div>
              </>
            )}
          </div>
          <SideNote sections={[
            ["What's next", <>Open the batch and hit <span style={{ color: "var(--text-1)" }}>Rank relevance</span> — Rasp ranks every lead against your topic{angle === "former" ? <>, and <span style={{ color: "var(--text-1)" }}>Score</span> rates recruit quality</> : null}. Then import the shortlist into a project.</>],
          ]} />
        </div>
      )}
    </section>
  );
}
